import {
  type QueryKey,
  type UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import * as React from "react";

// ---------------------------------------------------------------------------
// Auth-error interceptor — lets features register a side-effect callback
// when the API returns 401 or 403 without creating a circular import.
// ---------------------------------------------------------------------------

interface ApiInterceptors {
  onAuthError?: () => void;
}

const apiInterceptors: ApiInterceptors = {};

/**
 * Register a callback fired once whenever the API client receives an
 * HTTP 401 or 403 response.  Replaces the previous handler.  Pass
 * `undefined` to clear.  Intended for the SSE auth-invalidation wire
 * in `useAgentSession`.
 */
export function setApiAuthErrorInterceptor(
  handler: (() => void) | undefined,
): void {
  apiInterceptors.onAuthError = handler;
}

// Multi-listener auth-error bus — supports multiple simultaneous subscribers
// (e.g., active-session SSE client + N background-session SSE clients).
const authErrorListeners = new Set<() => void>();

/**
 * Subscribe a callback that fires whenever the API client receives a
 * 401 or 403.  Returns an unsubscribe function.  Multiple listeners
 * coexist; each receives the signal independently.  Use this instead of
 * `setApiAuthErrorInterceptor` when more than one subscriber needs the
 * signal concurrently (e.g., background SSE clients in
 * `useBackgroundSessionMonitor`).
 */
export function addApiAuthErrorListener(handler: () => void): () => void {
  authErrorListeners.add(handler);
  return () => authErrorListeners.delete(handler);
}

// Error handling
export interface APIErrorData {
  error?: string;
  error_code?: string;
  message?: string;
  /** DRF's own key, also used by some hand-written views. */
  detail?: string;
  [key: string]: unknown;
}

/**
 * Partial error for non-fatal issues during request processing.
 * Used when a request partially succeeds (e.g., data returned but metrics timed out).
 */
export interface PartialError {
  code: string;
  message: string;
}

const SERVED_SURFACE_ERROR_CODES = new Set([
  "serving_tables_unavailable",
  "invalid_request",
]);

/**
 * The sentence a Channel or Custom Channel surface should show for a served
 * read-path failure.
 *
 * `serving_tables_unavailable` is a missing star: retrying the same request
 * does not rebuild it. `invalid_request` names the token the caller has to
 * change. Both live on `error`, which is what the API client copies into
 * `APIError.message` when `message` is absent.
 */
export function servedSurfaceErrorMessage(error: unknown): string | null {
  if (!(error instanceof APIError)) return null;
  const code = error.data?.error_code;
  if (typeof code !== "string" || !SERVED_SURFACE_ERROR_CODES.has(code)) {
    return null;
  }
  const sentence = error.data?.error;
  if (typeof sentence === "string" && sentence.length > 0) return sentence;
  return error.message;
}

export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: APIErrorData | null,
    /** Seconds from a `Retry-After` header given in seconds. Undefined when
     * absent or unreadable: a cross-origin build reads it only if the API
     * lists it in `Access-Control-Expose-Headers`, so callers must treat
     * undefined as an unknown wait, not a default one. */
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "APIError";
  }
}

/** A `Retry-After` header's seconds; `undefined` for an HTTP date or junk. */
function retryAfterSeconds(header: string | null): number | undefined {
  if (header === null || !/^\d+$/.test(header.trim())) return undefined;
  return Number(header.trim());
}

// Types
type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

/**
 * Value of a single query parameter. An array is sent as repeated occurrences
 * of the same key (`?categories=A&categories=B`) rather than one joined value,
 * because a value may itself contain the separator — a category label may
 * contain a comma.
 */
export type QueryParamValue = string | string[];

type RequestOptions = RequestInit & {
  params?: Record<string, QueryParamValue>;
  /**
   * Do not notify the auth-error interceptor and listeners on a 401/403. For
   * probes that expect a 403 and handle it themselves, such as the
   * provisioning readiness check, so an expected deny does not tear down the
   * agent SSE clients.
   */
  skipAuthErrorSignal?: boolean;
};

// API Client configuration
//
// `API_BASE` is the prefix every API URL in the app must be built from,
// including EventSource / SSE URLs that bypass this module's `request()` path.
//
// It is the absolute backend origin (e.g.
// `https://dashboard.external.stage.upsidetechapi.com`) until an environment is
// flipped same-origin, and empty afterwards. `VITE_SAME_ORIGIN` is that flip
// (DSOR gate 3): once the Cloudflare Pages worker proxies `/api/*` on the
// dashboard origin itself, a relative prefix reaches the backend and the
// session cookies are first-party, which is the whole point — Safari ITP and
// incognito drop them cross-site.
//
// The flip deliberately does NOT empty `VITE_API_URL`. That variable is also
// what the Pages worker reads at runtime to know where to forward, and what
// Sentry derives its trace-propagation target from, so it must keep pointing at
// the real API host in every environment. One variable, one meaning.
export const API_BASE =
  import.meta.env.VITE_SAME_ORIGIN === "true"
    ? ""
    : import.meta.env.VITE_API_URL || "";

/**
 * Host that API requests actually reach, for consumers that must match on it
 * rather than build a URL — currently Sentry's `tracePropagationTargets`.
 *
 * This has to follow `API_BASE`, not `VITE_API_URL`. Once an environment is
 * flipped same-origin, `VITE_API_URL` still points at the backend (the Pages
 * worker needs it) but the browser no longer sends anything there, so matching
 * on that host would silently stop attaching trace headers to every
 * authenticated call.
 *
 * @param locationHost `window.location.host` — passed in so this stays pure.
 */
export function apiRequestHost(locationHost: string): string {
  return API_BASE ? new URL(API_BASE).host : locationHost;
}
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

// Get CSRF token from cookies
function getCsrfToken(): string | null {
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

// Utility to build URL with query parameters
function buildUrl(
  path: string,
  params?: Record<string, QueryParamValue>,
): string {
  const url = `${API_BASE}/api${path.startsWith("/") ? path : `/${path}`}`;
  if (!params) return url;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item != null) searchParams.append(key, item);
      }
      continue;
    }
    searchParams.append(key, value);
  }
  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export interface QueryGetOptions {
  signal?: AbortSignal;
}

/** A GET that resolves to the parsed body and rejects with `APIError`. */
export type ApiQueryGet = <T>(
  path: string,
  options?: QueryGetOptions,
) => Promise<T>;

/**
 * Where `api.useQuery` and `api.useQueryWithTransform` fetch from. Unset in the
 * dashboard, so they use `api.get` on the dashboard session. An embed page
 * provides its panel client here, so the dashboard's own data hooks render
 * inside a Salesforce panel without a dashboard session (DEV-2286). Direct
 * `api.get`/`api.post` calls are never redirected.
 */
export const ApiQueryGetContext = React.createContext<ApiQueryGet | null>(null);

// Query key generation
export const generateQueryKey = (path: string): QueryKey =>
  path.split("/").filter(Boolean);

// Main API client
/**
 * Raises the `APIError` for a failed response, signalling an auth error on a
 * 401 or 403 unless the caller opted out.
 */
async function throwResponseError(
  response: Response,
  skipAuthErrorSignal: boolean,
): Promise<never> {
  const errorData: APIErrorData | null = await response
    .json()
    .catch(() => null);
  if (
    !skipAuthErrorSignal &&
    (response.status === 401 || response.status === 403)
  ) {
    apiInterceptors.onAuthError?.();
    for (const listener of authErrorListeners) {
      listener();
    }
  }
  throw new APIError(
    errorSentence(errorData) ?? fallbackErrorMessage(response),
    response.status,
    errorData,
    retryAfterSeconds(response.headers.get("Retry-After")),
  );
}

/** A JSON error body read as text, or null when it is not a JSON object. */
function parsedErrorData(text: string): APIErrorData | null {
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as APIErrorData)
      : null;
  } catch {
    return null;
  }
}

/** Servers answer with `message`, `error` or `detail`, depending on the view. */
function errorSentence(errorData: APIErrorData | null): string | null {
  for (const key of ["message", "error", "detail"] as const) {
    const value = errorData?.[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

/** HTTP/2 has no reason phrase, so `statusText` alone can be empty. */
function fallbackErrorMessage(response: Response): string {
  return response.statusText
    ? `API Error: ${response.status} ${response.statusText}`
    : `API Error: ${response.status}`;
}

/** A file the server sent, with the name it asked for. */
export interface ServerFile {
  blob: Blob;
  /** Null when the server named no file, or its header is not readable here. */
  filename: string | null;
}

/** The filename in a `Content-Disposition` header, preferring `filename*`. */
export function filenameFromContentDisposition(
  header: string | null,
): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding: fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header);
  const name = (plain?.[1] ?? plain?.[2])?.trim();
  return name ? name : null;
}

export const api = {
  async request<T>(
    method: RequestMethod,
    path: string,
    {
      params,
      headers = {},
      skipAuthErrorSignal = false,
      ...config
    }: RequestOptions = {},
  ): Promise<T> {
    const requestHeaders = new Headers(headers);
    // FormData: the browser must set Content-Type itself to include the multipart boundary.
    if (
      !requestHeaders.has("Content-Type") &&
      !(config.body instanceof FormData)
    ) {
      requestHeaders.set("Content-Type", "application/json");
    }

    // Add CSRF token for non-GET requests
    if (method !== "GET") {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        requestHeaders.set("X-CSRFToken", csrfToken);
      }
    }

    const response = await fetch(buildUrl(path, params), {
      method,
      credentials: "include",
      headers: requestHeaders,
      ...config,
    });

    if (!response.ok) {
      await throwResponseError(response, skipAuthErrorSignal);
    }

    return response.status === 204 ? ({} as T) : response.json();
  },

  /**
   * GET a file as bytes, with the same credentials and error handling as
   * `request`, so a failure is an `APIError` rather than a page navigation.
   */
  async getBlob(
    path: string,
    { params, skipAuthErrorSignal = false, ...config }: RequestOptions = {},
  ): Promise<ServerFile> {
    const response = await fetch(buildUrl(path, params), {
      method: "GET",
      credentials: "include",
      ...config,
    });
    if (!response.ok) {
      await throwResponseError(response, skipAuthErrorSignal);
    }
    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(
        response.headers.get("Content-Disposition"),
      ),
    };
  },

  get: <T>(path: string, options?: RequestOptions) =>
    api.request<T>("GET", path, options),
  post: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("POST", path, { ...options, body: JSON.stringify(data) }),
  put: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("PUT", path, { ...options, body: JSON.stringify(data) }),
  patch: <T>(path: string, data?: unknown, options: RequestOptions = {}) =>
    api.request<T>("PATCH", path, { ...options, body: JSON.stringify(data) }),
  delete: <T>(path: string, options?: RequestOptions) =>
    api.request<T>("DELETE", path, options),
  postForm: <T>(path: string, body: FormData, options: RequestOptions = {}) =>
    api.request<T>("POST", path, { ...options, body }),

  /**
   * POST request that returns the raw Response for streaming.
   * Use this for endpoints that return streaming data (e.g., NDJSON).
   */
  async postStream(
    path: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<Response> {
    const { params, headers = {}, ...config } = options;
    const requestHeaders = new Headers({
      "Content-Type": "application/json",
      ...headers,
    });

    const csrfToken = getCsrfToken();
    if (csrfToken) {
      requestHeaders.set("X-CSRFToken", csrfToken);
    }

    const response = await fetch(buildUrl(path, params), {
      method: "POST",
      credentials: "include",
      headers: requestHeaders,
      body: JSON.stringify(data),
      ...config,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new APIError(
        `API Error: ${response.status} ${errorText}`,
        response.status,
        parsedErrorData(errorText),
      );
    }

    return response;
  },

  useQuery<TData>(
    path: string,
    options?: Omit<UseQueryOptions<TData, APIError>, "queryKey" | "queryFn">,
  ) {
    const get = React.useContext(ApiQueryGetContext) ?? api.get;
    return useQuery<TData, APIError>({
      queryKey: generateQueryKey(path),
      queryFn: () => get<TData>(path),
      staleTime: DEFAULT_STALE_TIME,
      ...options,
    });
  },

  useQueryWithTransform<TData, TRData>(
    path: string,
    transform: (data: TData) => TRData,
    options?: Omit<UseQueryOptions<TRData, APIError>, "queryKey" | "queryFn">,
  ) {
    const get = React.useContext(ApiQueryGetContext) ?? api.get;
    return useQuery<TRData, APIError>({
      queryKey: generateQueryKey(path),
      queryFn: () => get<TData>(path).then(transform),
      staleTime: DEFAULT_STALE_TIME,
      ...options,
    });
  },
};
